import { PrimitiveAtom, useAtom } from 'jotai';
import { useCallback, useEffect, useEffectEvent, useMemo } from 'react';
import { ListedInstrument } from '../../types';
import { isDefined, Loadable, LoadableState } from '../../utils';

export type InstrumentsByRic = Record<string, LoadableState<ListedInstrument>>;

export type UseListedInstrumentsByRicCommonParams = {
  rics: string[];
  atom: PrimitiveAtom<InstrumentsByRic>;
  getListedInstrumentList: (ricList: string[]) => Promise<Array<ListedInstrument>>;
};

export type UseListedInstrumentsByRicCommonOutput = {
  instrumentsByRic: InstrumentsByRic;
  reload: () => Promise<void>;
};

const createLoadableMapping = (
  rics: string[],
  mapper: (ric: string) => LoadableState<ListedInstrument>,
): InstrumentsByRic => {
  return rics.reduce<InstrumentsByRic>((loadables, ric) => ({ ...loadables, [ric]: mapper(ric) }), {});
};

export const useListedInstrumentsByRicCommon = ({
  rics,
  atom,
  getListedInstrumentList,
}: UseListedInstrumentsByRicCommonParams): UseListedInstrumentsByRicCommonOutput => {
  const [instruments, setInstruments] = useAtom(atom);

  const loadByRics = useCallback(
    async (ricsToLoad: string[]): Promise<void> => {
      if (ricsToLoad.length === 0) {
        return;
      }
      setInstruments(prev => ({ ...prev, ...createLoadableMapping(ricsToLoad, () => Loadable.loading) }));
      try {
        const instrumentList = await getListedInstrumentList(ricsToLoad);
        setInstruments(prev => ({
          ...prev,
          ...createLoadableMapping(ricsToLoad, ric => {
            const instrument = instrumentList.find(i => i.ric === ric);
            return isDefined(instrument) ? Loadable.loaded(instrument) : Loadable.failed;
          }),
        }));
      } catch (e) {
        setInstruments(prev => ({ ...prev, ...createLoadableMapping(ricsToLoad, () => Loadable.failed) }));
      }
    },
    [getListedInstrumentList, setInstruments],
  );

  const reload = useCallback(() => loadByRics(rics), [loadByRics, rics]);

  const shouldLoad = useEffectEvent((rics: string[]): boolean =>
    rics.some(ric => !isDefined(instruments[ric]) || (!instruments[ric]?.isLoaded && !instruments[ric]?.isLoading)),
  );

  const loadMissing = useEffectEvent((rics: string[]) => {
    const ricsToLoad = rics.filter(
      ric => !isDefined(instruments[ric]) || (!instruments[ric]?.isLoaded && !instruments[ric]?.isLoading),
    );
    loadByRics(ricsToLoad);
  });

  // Only load if we are missing any of the instruments present in the ric list.
  useEffect(() => {
    if (!shouldLoad(rics)) {
      return;
    }
    loadMissing(rics);
  }, [rics]);

  const instrumentsByRic = useMemo(
    () => createLoadableMapping(rics, ric => instruments[ric] ?? Loadable.uninitialized),
    [instruments, rics],
  );

  return {
    instrumentsByRic,
    reload,
  };
};
